import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import Autocomplete from './ui/autocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { 
  Calendar,
  CalendarDays,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  Plus,
  Calculator,
  AlertCircle,
  XCircle,
  Briefcase,
  RefreshCw,
  Upload,
  Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VacationControlPage = () => {
  const [employees, setEmployees] = useState([]);
  const [vacationPeriods, setVacationPeriods] = useState([]);
  const [vacationRequests, setVacationRequests] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Form states for vacation request
  const [requestForm, setRequestForm] = useState({
    employee_id: '',
    vacation_period_id: '',
    start_date: '',
    end_date: '',
    days_of_enjoyment: 0, // Dias de Gozo
    notes: '',
    sell_vacation_days: false,
    days_to_sell: 0,
    request_13th_advance: false
  });

  // Estados para Histórico de Férias
  const [historico, setHistorico] = useState([]);
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [historicoForm, setHistoricoForm] = useState({
    employee_id: '',
    periodo_aquisitivo_inicio: '',
    periodo_aquisitivo_fim: '',
    data_inicio_gozo: '',
    data_fim_gozo: '',
    dias_gozados: 30,
    status: 'gozado',
    observacoes: ''
  });
  const [csvFile, setCsvFile] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Reload data when tab changes to ensure fresh data
  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'periods-clt' || activeTab === 'periods-pj') {
      loadVacationPeriods();
      loadVacationRequests();
      loadEmployees(); // Recarregar funcionários também
    }
    if (activeTab === 'history' && selectedEmployee) {
      loadHistorico(selectedEmployee);
    }
    if (activeTab === 'alteracoes') {
      loadVacationChangeRequests();
    }
  }, [activeTab]);

  // Force reload all data
  const forceReloadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadEmployees(),
        loadVacationPeriods(),
        loadVacationRequests(),
        loadVacationChangeRequests()
      ]);
    } catch (error) {
      console.error('Erro ao recarregar dados:', error);
      toast.error('Erro ao recarregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadEmployees(),
        loadVacationPeriods(),
        loadVacationRequests(),
        loadVacationChangeRequests()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados de férias');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`);
      setEmployees(response.data.filter(emp => emp.situacao === 'ATIVO'));
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const loadVacationPeriods = async () => {
    try {
      const response = await axios.get(`${API}/api/vacations/periods`);
      setVacationPeriods(response.data);
    } catch (error) {
      console.error('Erro ao carregar períodos de férias:', error);
    }
  };

  const loadVacationRequests = async () => {
    try {
      const response = await axios.get(`${API}/api/vacations/requests`);
      setVacationRequests(response.data);
    } catch (error) {
      console.error('Erro ao carregar solicitações de férias:', error);
    }
  };

  const loadVacationChangeRequests = async () => {
    try {
      const response = await axios.get(`${API}/api/vacations/change-requests`);
      setChangeRequests(response.data);
    } catch (error) {
      console.error('Erro ao carregar solicitações de alteração:', error);
    }
  };

  const approveChangeRequest = async (changeId) => {
    try {
      await axios.put(`${API}/api/vacations/change-requests/${changeId}/approve`);
      toast.success('Solicitação aprovada com sucesso!');
      await Promise.all([loadVacationRequests(), loadVacationPeriods(), loadVacationChangeRequests()]);
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao aprovar solicitação');
    }
  };

  const rejectChangeRequest = async (changeId) => {
    try {
      await axios.put(`${API}/api/vacations/change-requests/${changeId}/reject`);
      toast.success('Solicitação rejeitada');
      await loadVacationChangeRequests();
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar solicitação');
    }
  };

  const calculateVacationPeriods = async (employeeId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/api/vacations/periods/calculate?employee_id=${employeeId}`);
      toast.success('Períodos de férias calculados com sucesso!');
      await loadVacationPeriods();
    } catch (error) {
      console.error('Erro ao calcular períodos:', error);
      toast.error('Erro ao calcular períodos de férias');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    try {
      if (!requestForm.employee_id || !requestForm.vacation_period_id || !requestForm.start_date || !requestForm.end_date) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      if (requestForm.end_date < requestForm.start_date) {
        toast.error('A data fim não pode ser menor que a data início');
        return;
      }

      await axios.post(`${API}/api/vacations/requests`, requestForm);
      toast.success('Solicitação de férias criada com sucesso!');
      
      setShowRequestModal(false);
      setRequestForm({
        employee_id: '',
        vacation_period_id: '',
        start_date: '',
        end_date: '',
        days_of_enjoyment: 0,
        notes: '',
        sell_vacation_days: false,
        days_to_sell: 0,
        request_13th_advance: false
      });
      
      await loadVacationRequests();
      await loadVacationPeriods();
    } catch (error) {
      console.error('Erro ao criar solicitação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar solicitação de férias');
    }
  };

  const approveRequest = async (requestId) => {
    try {
      await axios.put(`${API}/api/vacations/requests/${requestId}/approve`);
      toast.success('Férias aprovadas com sucesso!');
      await loadVacationRequests();
      await loadVacationPeriods();
    } catch (error) {
      console.error('Erro ao aprovar férias:', error);
      toast.error('Erro ao aprovar férias');
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await axios.put(`${API}/api/vacations/requests/${requestId}/reject`);
      toast.success('Solicitação de férias rejeitada');
      await loadVacationRequests();
    } catch (error) {
      console.error('Erro ao rejeitar férias:', error);
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar férias');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const datePart = dateString.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length !== 3) return dateString;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { variant: 'default', label: 'Ativo', color: 'bg-green-500' },
      'pending': { variant: 'secondary', label: 'Pendente', color: 'bg-yellow-500' },
      'expired': { variant: 'destructive', label: 'Vencido', color: 'bg-red-500' },
      'completed': { variant: 'outline', label: 'Concluído', color: 'bg-blue-500' },
      'approved': { variant: 'default', label: 'Aprovado', color: 'bg-green-500' },
      'rejected': { variant: 'destructive', label: 'Rejeitado', color: 'bg-red-500' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getUrgencyLevel = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0) return { level: 'Vencido', color: 'text-red-600 bg-red-50', icon: XCircle };
    if (daysUntilExpiry <= 30) return { level: 'Urgente', color: 'text-red-600 bg-red-50', icon: AlertTriangle };
    if (daysUntilExpiry <= 90) return { level: 'Atenção', color: 'text-yellow-600 bg-yellow-50', icon: AlertCircle };
    return { level: 'Normal', color: 'text-green-600 bg-green-50', icon: CheckCircle };
  };

  const getEmployeeVacationSummary = (employeeId) => {
    const employeePeriods = vacationPeriods.filter(p => p.employee_id === employeeId);
    const activePeriods = employeePeriods.filter(p => p.status === 'active');
    const expiredPeriods = employeePeriods.filter(p => p.status === 'expired');
    
    return {
      totalPeriods: employeePeriods.length,
      activePeriods: activePeriods.length,
      expiredPeriods: expiredPeriods.length,
      totalDaysAvailable: activePeriods.reduce((sum, p) => sum + p.days_remaining, 0)
    };
  };

  const calculateVacationBalance = () => {
    if (!requestForm.vacation_period_id) {
      return { currentBalance: 0, balanceAfterRequest: 0, requestedDays: 0 };
    }

    const selectedPeriod = vacationPeriods.find(p => p.id === requestForm.vacation_period_id);
    if (!selectedPeriod) {
      return { currentBalance: 0, balanceAfterRequest: 0, requestedDays: 0 };
    }

    // Use days_of_enjoyment if available, otherwise calculate from dates
    let requestedDays = 0;
    if (requestForm.days_of_enjoyment > 0) {
      requestedDays = requestForm.days_of_enjoyment;
    } else if (requestForm.start_date && requestForm.end_date) {
      const startDate = new Date(requestForm.start_date);
      const endDate = new Date(requestForm.end_date);
      requestedDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    }
    
    const currentBalance = selectedPeriod.days_remaining;
    const balanceAfterRequest = currentBalance - requestedDays;

    return { currentBalance, balanceAfterRequest, requestedDays };
  };

  // Calculate end date based on start date and days of enjoyment
  const calculateEndDate = (startDate, daysOfEnjoyment) => {
    if (!startDate || !daysOfEnjoyment || daysOfEnjoyment <= 0) return '';
    
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(start.getDate() + daysOfEnjoyment - 1); // -1 because start date counts as day 1
    
    return end.toISOString().split('T')[0];
  };

  // Handle start date or days of enjoyment change
  const handleDateCalculation = (field, value) => {
    const newForm = { ...requestForm, [field]: value };
    
    if (field === 'start_date' || field === 'days_of_enjoyment') {
      if (newForm.start_date && newForm.days_of_enjoyment > 0) {
        newForm.end_date = calculateEndDate(newForm.start_date, newForm.days_of_enjoyment);
      }
    }
    
    setRequestForm(newForm);
  };

  // ===== FUNÇÕES DO HISTÓRICO DE FÉRIAS =====
  
  const loadHistorico = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/vacations/history/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorico(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico de férias');
    }
  };

  const handleCreateHistorico = async () => {
    try {
      if (!historicoForm.employee_id || !historicoForm.periodo_aquisitivo_inicio || !historicoForm.periodo_aquisitivo_fim) {
        toast.error('Preencha os campos obrigatórios');
        return;
      }

      const employeeIdToLoad = historicoForm.employee_id; // Salvar antes de resetar
      
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/vacations/history`, historicoForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Histórico cadastrado com sucesso!');
      
      setShowHistoricoModal(false);
      setHistoricoForm({
        employee_id: '',
        periodo_aquisitivo_inicio: '',
        periodo_aquisitivo_fim: '',
        data_inicio_gozo: '',
        data_fim_gozo: '',
        dias_gozados: 30,
        status: 'gozado',
        observacoes: ''
      });
      
      // Recarregar histórico do funcionário que acabou de cadastrar
      setSelectedEmployee(employeeIdToLoad);
      await loadHistorico(employeeIdToLoad);
    } catch (error) {
      console.error('Erro ao cadastrar histórico:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar histórico');
    }
  };

  const handleDeleteHistorico = async (historicoId, employeeId) => {
    if (!window.confirm('Tem certeza que deseja deletar este histórico?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/vacations/history/${historicoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Histórico deletado com sucesso!');
      await loadHistorico(employeeId);
    } catch (error) {
      console.error('Erro ao deletar histórico:', error);
      toast.error('Erro ao deletar histórico');
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      toast.error('Selecione um arquivo CSV');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(`${API}/api/vacations/history/import-csv`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success(`Importação concluída: ${response.data.importados} registros importados`);
      
      if (response.data.erros && response.data.erros.length > 0) {
        console.warn('Erros na importação:', response.data.erros);
        toast.warning(`${response.data.erros.length} erros encontrados. Verifique o console.`);
      }

      setShowImportModal(false);
      setCsvFile(null);
      
      // Recarregar histórico se algum funcionário estiver selecionado
      if (selectedEmployee) {
        await loadHistorico(selectedEmployee);
      }
    } catch (error) {
      console.error('Erro ao importar CSV:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar CSV');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span>Carregando controle de férias...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Controle de Férias</h1>
          <p className="text-slate-600 mt-1">
            Gerencie períodos de férias e aquisições conforme a legislação trabalhista
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogTrigger asChild>
            <Button className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Nova Solicitação</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nova Solicitação de Férias</DialogTitle>
              <DialogDescription>
                Criar uma nova solicitação de férias para um funcionário
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="employee">Funcionário</Label>
                <Autocomplete
                  value={requestForm.employee_id}
                  onValueChange={(value) => setRequestForm({...requestForm, employee_id: value, vacation_period_id: ''})}
                  options={(() => {
                    // Filtrar funcionários baseado na aba ativa
                    let filteredEmployees = employees.filter(e => e.situacao === 'ATIVO');

                    if (activeTab === 'periods-pj') {
                      // Na aba PJ, mostrar apenas funcionários PJ
                      filteredEmployees = filteredEmployees.filter(e => e.regime === 'PJ');
                    } else if (activeTab === 'periods-clt') {
                      // Na aba CLT, mostrar apenas funcionários CLT (ou sem regime definido)
                      filteredEmployees = filteredEmployees.filter(e => e.regime === 'CLT' || !e.regime);
                    }
                    // Na aba Visão Geral ou outras, mostrar todos

                    return filteredEmployees;
                  })()}
                  getOptionValue={(employee) => employee.id}
                  getOptionLabel={(employee) => `${employee.nome} - ${employee.cargo}${employee.regime === 'PJ' ? ' (PJ)' : employee.regime === 'CLT' ? ' (CLT)' : ''}`}
                  placeholder="Selecione o funcionário"
                  emptyMessage="Nenhum funcionário encontrado"
                />
                {(activeTab === 'periods-pj' || activeTab === 'periods-clt') && (
                  <p className="text-xs text-gray-500">
                    Mostrando apenas funcionários {activeTab === 'periods-pj' ? 'PJ' : 'CLT'}
                  </p>
                )}
              </div>
              
              {requestForm.employee_id && (
  <div className="space-y-2">
    <Label htmlFor="period">Período de Férias</Label>
    {(() => {
      const periodosDisponiveis = vacationPeriods.filter(period => {
        if (period.employee_id !== requestForm.employee_id) return false;
        // Ativos e pendentes podem receber solicitações antecipadas.
        if (['active', 'pending'].includes(period.status)) return period.days_remaining > 0;
        // Vencidos: backend zera days_remaining, então o saldo real é earned - taken
        if (period.status === 'expired') return (period.days_earned - (period.days_taken || 0)) > 0;
        return false;
      });

      if (periodosDisponiveis.length === 0) {
        return (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="font-medium">Nenhum período disponível para este funcionário.</p>
            <p className="mt-1">
              Possíveis causas: períodos ainda não calculados, vencidos ou sem saldo de dias.
              Use o botão "Calcular" na Visão Geral para gerar os períodos.
            </p>
          </div>
        );
      }

      return (
        <Select value={requestForm.vacation_period_id} onValueChange={(value) => setRequestForm({...requestForm, vacation_period_id: value})}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione o período" />
          </SelectTrigger>
          <SelectContent>
            {periodosDisponiveis.map(period => {
              const isVencido = period.status === 'expired';
              const diasDisponiveis = isVencido 
                ? period.days_earned - (period.days_taken || 0) 
                : period.days_remaining;
              return (
                <SelectItem key={period.id} value={period.id}>
                  {formatDate(period.acquisition_start)} - {formatDate(period.acquisition_end)} ({diasDisponiveis} dias disponíveis){isVencido ? ' ⚠️ VENCIDO' : ''}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      );
    })()}
  </div>
)}
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Data Início</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={requestForm.start_date}
                    onChange={(e) => handleDateCalculation('start_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="days_of_enjoyment">Dias de Gozo</Label>
                  <Input
                    id="days_of_enjoyment"
                    type="number"
                    min="1"
                    max="30"
                    value={requestForm.days_of_enjoyment || ''}
                    onChange={(e) => handleDateCalculation('days_of_enjoyment', parseInt(e.target.value) || 0)}
                    placeholder="Ex: 15"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Data Fim</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={requestForm.end_date}
                    onChange={(e) => setRequestForm({...requestForm, end_date: e.target.value})}
                    className="bg-gray-50"
                    readOnly
                  />
                  <p className="text-xs text-gray-500">Calculado automaticamente</p>
                </div>
              </div>

              {/* Vacation Balance Information */}
              {requestForm.vacation_period_id && requestForm.start_date && requestForm.end_date && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Saldo de Férias</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-600">Saldo Atual de dias:</span>
                      <span className="font-bold ml-2">{calculateVacationBalance().currentBalance} dias</span>
                    </div>
                    <div>
                      <span className="text-blue-600">Saldo após solicitação:</span>
                      <span className="font-bold ml-2">{calculateVacationBalance().balanceAfterRequest} dias</span>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-blue-600">
                    <span>Dias solicitados: {calculateVacationBalance().requestedDays} dias</span>
                  </div>
                </div>
              )}

              {/* Sell Vacation Days - Only for CLT */}
              {(() => {
                const selectedEmployee = employees.find(e => e.id === requestForm.employee_id);
                const isPJ = selectedEmployee?.regime === 'PJ';
                const selectedPeriod = vacationPeriods.find(p => p.id === requestForm.vacation_period_id);
                const canSellDays = !isPJ && selectedPeriod?.can_sell_days !== false;
                
                return (
                  <div className="space-y-3">
                    {isPJ && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                        <p className="text-purple-800">
                          ℹ️ <strong>Funcionário PJ:</strong> Não é permitida a venda de dias de férias.
                        </p>
                      </div>
                    )}
                    
                    {!isPJ && (
                      <>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="sell_vacation_days"
                            checked={requestForm.sell_vacation_days}
                            disabled={!canSellDays}
                            onCheckedChange={(checked) => setRequestForm({
                              ...requestForm, 
                              sell_vacation_days: checked,
                              days_to_sell: checked ? 1 : 0
                            })}
                          />
                          <Label htmlFor="sell_vacation_days" className="text-sm font-medium">
                            Vender alguns dias de férias? (CLT)
                          </Label>
                        </div>
                        
                        {requestForm.sell_vacation_days && (
                          <div className="ml-6 space-y-2">
                            <Label htmlFor="days_to_sell">Quantidade de dias vendidos</Label>
                            <Select 
                              value={requestForm.days_to_sell.toString()} 
                              onValueChange={(value) => setRequestForm({...requestForm, days_to_sell: parseInt(value)})}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Selecione a quantidade de dias" />
                              </SelectTrigger>
                              <SelectContent>
                                {[1,2,3,4,5,6,7,8,9,10].map(days => (
                                  <SelectItem key={days} value={days.toString()}>
                                    {days} {days === 1 ? 'dia' : 'dias'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">
                              Máximo permitido: 10 dias (1/3 das férias)
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Request 13th Salary Advance */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="request_13th_advance"
                  checked={requestForm.request_13th_advance}
                  onCheckedChange={(checked) => setRequestForm({...requestForm, request_13th_advance: checked})}
                />
                <Label htmlFor="request_13th_advance" className="text-sm font-medium">
                  Solicitar Antecipação da 1ª Parcela do 13º?
                </Label>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Observações</Label>
                <Textarea
                  id="notes"
                  placeholder="Observações adicionais..."
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm({...requestForm, notes: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateRequest}>
                Criar Solicitação
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full items-center justify-start gap-1 overflow-x-auto sm:grid sm:grid-cols-7 sm:overflow-x-visible">
          <TabsTrigger value="overview" className="flex-shrink-0">Visão Geral</TabsTrigger>
          <TabsTrigger value="periods-clt" className="flex-shrink-0">CLT</TabsTrigger>
          <TabsTrigger value="periods-pj" className="flex-shrink-0">PJ</TabsTrigger>
          <TabsTrigger value="solicitacoes" className="flex-shrink-0">Solicitações</TabsTrigger>
          <TabsTrigger value="programadas" className="flex-shrink-0">Programadas</TabsTrigger>
          <TabsTrigger value="alteracoes" className="flex-shrink-0">Alterações</TabsTrigger>
          <TabsTrigger value="history" className="flex-shrink-0">Histórico</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <User className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold">{employees.filter(e => e.situacao === 'ATIVO' && (e.regime === 'CLT' || !e.regime)).length}</p>
                    <p className="text-xs text-gray-500">Funcionários CLT</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <User className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="text-2xl font-bold">{employees.filter(e => e.situacao === 'ATIVO' && e.regime === 'PJ').length}</p>
                    <p className="text-xs text-gray-500">Funcionários PJ</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold">{vacationPeriods.filter(p => p.status === 'active').length}</p>
                    <p className="text-xs text-gray-500">Períodos Ativos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                  <div>
                    <p className="text-2xl font-bold">{vacationPeriods.filter(p => p.status === 'expired').length}</p>
                    <p className="text-xs text-gray-500">Períodos Vencidos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <Clock className="h-8 w-8 text-yellow-600" />
                  <div>
                    <p className="text-2xl font-bold">{vacationRequests.filter(r => r.status === 'pending').length}</p>
                    <p className="text-xs text-gray-500">Solicitações Pendentes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Employee Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Funcionários - Situação das Férias</CardTitle>
              <CardDescription>
                Resumo da situação de férias de cada funcionário (CLT: 30 dias | PJ: 15 dias)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {employees.map(employee => {
                  const summary = getEmployeeVacationSummary(employee.id);
                  const activePeriods = vacationPeriods.filter(p => p.employee_id === employee.id && p.status === 'active');
                  const nextExpiry = activePeriods.length > 0 ? 
                    Math.min(...activePeriods.map(p => Math.ceil((new Date(p.concession_end) - new Date()) / (1000 * 60 * 60 * 24)))) : null;
                  
                  const urgency = nextExpiry !== null ? getUrgencyLevel(nextExpiry) : null;
                  const UrgencyIcon = urgency?.icon;

                  return (
                    <div key={employee.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{employee.nome}</p>
                          <p className="text-sm text-gray-500">{employee.cargo}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                        <div className="text-center">
                          <p className="text-sm font-medium">{summary.totalDaysAvailable}</p>
                          <p className="text-xs text-gray-500">Dias Disponíveis</p>
                        </div>

                        {urgency && (
                          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${urgency.color}`}>
                            <UrgencyIcon className="h-4 w-4" />
                            <span className="text-sm font-medium whitespace-nowrap">
                              {nextExpiry < 0 ? `Vencido há ${Math.abs(nextExpiry)} dias` : `${nextExpiry} dias`}
                            </span>
                          </div>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => calculateVacationPeriods(employee.id)}
                          className="flex items-center space-x-1"
                        >
                          <Calculator className="h-4 w-4" />
                          <span>Calcular</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CLT Periods Tab */}
        <TabsContent value="periods-clt" className="space-y-6">
          <Card>
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    Períodos de Férias - CLT
                  </CardTitle>
                  <CardDescription className="text-blue-100">
                    Regime CLT: 30 dias de férias por período aquisitivo (pode vender até 10 dias)
                  </CardDescription>
                </div>
                <Button
                  onClick={async () => {
                    const funcionariosCLT = employees.filter(e => e.situacao === 'ATIVO' && (e.regime === 'CLT' || !e.regime));
                    if (funcionariosCLT.length === 0) {
                      toast.info('Nenhum funcionário CLT ativo encontrado');
                      return;
                    }
                    if (window.confirm(`Recalcular períodos dos ${funcionariosCLT.length} funcionários CLT ativos?`)) {
                      setLoading(true);
                      try {
                        for (const emp of funcionariosCLT) {
                          await calculateVacationPeriods(emp.id);
                        }
                        await loadVacationPeriods();
                        toast.success(`Períodos CLT recalculados para ${funcionariosCLT.length} funcionários!`);
                      } catch (error) {
                        console.error('Erro ao recalcular:', error);
                        toast.error('Erro ao recalcular períodos');
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="bg-blue-800 hover:bg-blue-900"
                  size="sm"
                  disabled={loading}
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? 'Calculando...' : 'Recalcular CLT'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                // Filtrar apenas funcionários CLT
                const funcionariosCLT = employees.filter(e => e.situacao === 'ATIVO' && (e.regime === 'CLT' || !e.regime));
                console.log('📊 DEBUG CLT - Funcionários CLT:', funcionariosCLT.length);
                
                const periodosAtivos = vacationPeriods.filter(period => {
                  const employee = employees.find(e => e.id === period.employee_id);
                  return employee && employee.situacao === 'ATIVO' && (employee.regime === 'CLT' || !employee.regime);
                });
                
                console.log('📋 DEBUG CLT - Períodos filtrados:', periodosAtivos.length);
                
                // Identificar funcionários CLT sem períodos
                const funcionariosComPeriodos = new Set(periodosAtivos.map(p => p.employee_id));
                const funcionariosSemPeriodos = funcionariosCLT.filter(emp => !funcionariosComPeriodos.has(emp.id));

                if (periodosAtivos.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 text-lg mb-2">Nenhum período de férias CLT encontrado</p>
                      <p className="text-gray-500 text-sm mb-4">
                        Os períodos serão calculados automaticamente para funcionários CLT ativos
                      </p>
                      <Button
                        onClick={async () => {
                          setLoading(true);
                          try {
                            for (const emp of funcionariosCLT) {
                              await calculateVacationPeriods(emp.id);
                            }
                            toast.success('Períodos CLT calculados com sucesso!');
                          } catch (error) {
                            toast.error('Erro ao calcular períodos');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Calculator className="h-4 w-4 mr-2" />
                        Calcular Períodos CLT
                      </Button>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <p className="text-blue-800">
                        📊 Exibindo <strong>{periodosAtivos.length}</strong> período(s) de <strong>{funcionariosCLT.length}</strong> funcionário(s) CLT ativo(s)
                        {' '}| <strong>30 dias</strong> por período
                      </p>
                    </div>
                    
                    {funcionariosSemPeriodos.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-yellow-800 text-sm">
                              <strong>{funcionariosSemPeriodos.length} funcionário(s) CLT sem períodos calculados.</strong>
                              {' '}Clique em "Recalcular CLT" para calcular os períodos.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {periodosAtivos.map(period => {
                      const employee = employees.find(e => e.id === period.employee_id);
                      const daysUntilExpiry = Math.ceil((new Date(period.concession_end) - new Date()) / (1000 * 60 * 60 * 24));
                      
                      return (
                        <div key={period.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium">{employee?.nome || 'Funcionário não encontrado'}</h4>
                              <p className="text-sm text-gray-500">{employee?.cargo}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">CLT</Badge>
                              {period.can_sell_days !== false && (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Pode vender</Badge>
                              )}
                              {getStatusBadge(period.status)}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Período Aquisitivo</p>
                              <p className="font-medium">{formatDate(period.acquisition_start)} - {formatDate(period.acquisition_end)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Período Concessivo</p>
                              <p className="font-medium">{formatDate(period.concession_start)} - {formatDate(period.concession_end)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Dias Restantes</p>
                              <p className="font-medium text-blue-600">{period.days_remaining} de {period.days_earned || 30}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Prazo</p>
                              <p className={`font-medium ${daysUntilExpiry < 30 ? 'text-red-600' : daysUntilExpiry < 90 ? 'text-yellow-600' : 'text-green-600'}`}>
                                {daysUntilExpiry < 0 ? `Vencido há ${Math.abs(daysUntilExpiry)} dias` : `${daysUntilExpiry} dias`}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PJ Periods Tab */}
        <TabsContent value="periods-pj" className="space-y-6">
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    Períodos de Férias - PJ
                  </CardTitle>
                  <CardDescription className="text-purple-100">
                    Regime PJ: 15 dias de férias por período aquisitivo (sem venda de dias)
                  </CardDescription>
                </div>
                <Button
                  onClick={async () => {
                    const funcionariosPJ = employees.filter(e => e.situacao === 'ATIVO' && e.regime === 'PJ');
                    if (funcionariosPJ.length === 0) {
                      toast.info('Nenhum funcionário PJ ativo encontrado');
                      return;
                    }
                    if (window.confirm(`Recalcular períodos dos ${funcionariosPJ.length} funcionários PJ ativos?`)) {
                      setLoading(true);
                      try {
                        for (const emp of funcionariosPJ) {
                          await calculateVacationPeriods(emp.id);
                        }
                        await loadVacationPeriods();
                        toast.success(`Períodos PJ recalculados para ${funcionariosPJ.length} funcionários!`);
                      } catch (error) {
                        console.error('Erro ao recalcular:', error);
                        toast.error('Erro ao recalcular períodos PJ');
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="bg-purple-800 hover:bg-purple-900"
                  size="sm"
                  disabled={loading}
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? 'Calculando...' : 'Recalcular PJ'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {(() => {
                // Filtrar apenas funcionários PJ
                const funcionariosPJ = employees.filter(e => e.situacao === 'ATIVO' && e.regime === 'PJ');
                console.log('📊 DEBUG PJ - Funcionários PJ:', funcionariosPJ.length);
                
                const periodosAtivos = vacationPeriods.filter(period => {
                  const employee = employees.find(e => e.id === period.employee_id);
                  return employee && employee.situacao === 'ATIVO' && employee.regime === 'PJ';
                });
                
                console.log('📋 DEBUG PJ - Períodos filtrados:', periodosAtivos.length);
                
                // Identificar funcionários PJ sem períodos
                const funcionariosComPeriodos = new Set(periodosAtivos.map(p => p.employee_id));
                const funcionariosSemPeriodos = funcionariosPJ.filter(emp => !funcionariosComPeriodos.has(emp.id));

                if (funcionariosPJ.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <User className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 text-lg mb-2">Nenhum funcionário PJ ativo</p>
                      <p className="text-gray-500 text-sm">
                        Cadastre funcionários com regime PJ para visualizar seus períodos de férias
                      </p>
                    </div>
                  );
                }

                if (periodosAtivos.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 text-lg mb-2">Nenhum período de férias PJ encontrado</p>
                      <p className="text-gray-500 text-sm mb-4">
                        Os períodos serão calculados automaticamente para funcionários PJ ativos
                      </p>
                      <Button
                        onClick={async () => {
                          setLoading(true);
                          try {
                            for (const emp of funcionariosPJ) {
                              await calculateVacationPeriods(emp.id);
                            }
                            toast.success('Períodos PJ calculados com sucesso!');
                            await loadVacationPeriods();
                          } catch (error) {
                            toast.error('Erro ao calcular períodos');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Calculator className="h-4 w-4 mr-2" />
                        Calcular Períodos PJ
                      </Button>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                      <p className="text-purple-800">
                        📊 Exibindo <strong>{periodosAtivos.length}</strong> período(s) de <strong>{funcionariosPJ.length}</strong> funcionário(s) PJ ativo(s)
                        {' '}| <strong>15 dias</strong> por período | <span className="text-red-600 font-medium">Sem venda de dias</span>
                      </p>
                    </div>
                    
                    {funcionariosSemPeriodos.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-yellow-800 text-sm">
                              <strong>{funcionariosSemPeriodos.length} funcionário(s) PJ sem períodos calculados.</strong>
                              {' '}Clique em "Recalcular PJ" para calcular os períodos.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {periodosAtivos.map(period => {
                      const employee = employees.find(e => e.id === period.employee_id);
                      const daysUntilExpiry = Math.ceil((new Date(period.concession_end) - new Date()) / (1000 * 60 * 60 * 24));
                      
                      return (
                        <div key={period.id} className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-purple-50/30">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium">{employee?.nome || 'Funcionário não encontrado'}</h4>
                              <p className="text-sm text-gray-500">{employee?.cargo}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">PJ</Badge>
                              <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">Não pode vender</Badge>
                              {getStatusBadge(period.status)}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Período Aquisitivo</p>
                              <p className="font-medium">{formatDate(period.acquisition_start)} - {formatDate(period.acquisition_end)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Período Concessivo</p>
                              <p className="font-medium">{formatDate(period.concession_start)} - {formatDate(period.concession_end)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Dias Restantes</p>
                              <p className="font-medium text-purple-600">{period.days_remaining} de {period.days_earned || 15}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Prazo</p>
                              <p className={`font-medium ${daysUntilExpiry < 30 ? 'text-red-600' : daysUntilExpiry < 90 ? 'text-yellow-600' : 'text-green-600'}`}>
                                {daysUntilExpiry < 0 ? `Vencido há ${Math.abs(daysUntilExpiry)} dias` : `${daysUntilExpiry} dias`}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nova Aba: Solicitações Pendentes */}
        <TabsContent value="solicitacoes" className="space-y-6">
          <Card>
            <CardHeader className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
              <CardTitle>Solicitações Pendentes de Aprovação</CardTitle>
              <CardDescription className="text-yellow-100">
                Pedidos de férias aguardando aprovação do RH
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {(() => {
                const solicitacoesPendentes = vacationRequests.filter(r => r.status === 'pending');
                
                if (solicitacoesPendentes.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <CheckCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 text-lg mb-2">Nenhuma solicitação pendente</p>
                      <p className="text-gray-500 text-sm">
                        Todas as solicitações de férias foram processadas
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                      <p className="text-yellow-800">
                        ⏳ <strong>{solicitacoesPendentes.length}</strong> solicitação(ões) aguardando aprovação
                      </p>
                    </div>
                    
                    {solicitacoesPendentes.map(request => {
                      const employee = employees.find(e => e.id === request.employee_id);
                      
                      return (
                        <div key={request.id} className="border-2 border-yellow-200 rounded-lg p-4 bg-yellow-50 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium text-lg">{request.employee_name || employee?.nome}</h4>
                              <p className="text-sm text-gray-600">{employee?.cargo}</p>
                              <p className="text-sm text-gray-700 mt-1">
                                📅 {formatDate(request.start_date)} - {formatDate(request.end_date)} 
                                <span className="font-semibold ml-2">({request.days_requested} dias)</span>
                              </p>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                onClick={() => approveRequest(request.id)}
                                className="bg-green-600 hover:bg-green-700 flex items-center space-x-2"
                              >
                                <CheckCircle className="h-4 w-4" />
                                <span>Aprovar</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  if (window.confirm('Tem certeza que deseja rejeitar esta solicitação?')) {
                                    rejectRequest(request.id);
                                  }
                                }}
                                className="flex items-center space-x-2"
                              >
                                <XCircle className="h-4 w-4" />
                                <span>Rejeitar</span>
                              </Button>
                            </div>
                          </div>
                          
                          {/* Informações Adicionais */}
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm bg-white p-3 rounded-lg">
                            {request.sell_vacation_days && (
                              <div className="flex items-center space-x-2 text-blue-600">
                                <span className="font-medium">💰 Venda de férias:</span>
                                <span>{request.days_to_sell} dias</span>
                              </div>
                            )}
                            {request.request_13th_advance && (
                              <div className="flex items-center space-x-2 text-green-600">
                                <span className="font-medium">💵 Abono 13º:</span>
                                <span>Sim</span>
                              </div>
                            )}
                            {request.notes && (
                              <div className="col-span-full">
                                <span className="font-medium text-gray-700">📝 Observações:</span>
                                <p className="text-gray-600 mt-1">{request.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Programadas (antiga "Solicitações") */}
        <TabsContent value="programadas" className="space-y-6">
          <Card>
            <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white">
              <CardTitle>Férias Programadas (Aprovadas)</CardTitle>
              <CardDescription className="text-green-100">
                Solicitações de férias já aprovadas pelo RH
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {(() => {
                const feriasAprovadas = vacationRequests.filter(r => r.status === 'approved');
                
                if (feriasAprovadas.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 text-lg mb-2">Nenhuma férias programada</p>
                      <p className="text-gray-500 text-sm">
                        Férias aprovadas aparecerão aqui
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                      <p className="text-green-800">
                        ✅ <strong>{feriasAprovadas.length}</strong> férias programada(s)
                      </p>
                    </div>
                    
                    {feriasAprovadas.map(request => {
                      const employee = employees.find(e => e.id === request.employee_id);
                      
                      return (
                        <div key={request.id} className="border rounded-lg p-4 bg-green-50 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium text-lg">{request.employee_name || employee?.nome}</h4>
                              <p className="text-sm text-gray-600">{employee?.cargo}</p>
                              <p className="text-sm text-gray-700 mt-1">
                                📅 {formatDate(request.start_date)} - {formatDate(request.end_date)} 
                                <span className="font-semibold ml-2">({request.days_requested} dias)</span>
                              </p>
                            </div>
                            {getStatusBadge(request.status)}
                          </div>
                      
                      {/* Additional Information */}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        {request.sell_vacation_days && (
                          <div className="flex items-center space-x-2 text-blue-600">
                            <span className="font-medium">Venda de férias:</span>
                            <span>{request.days_to_sell} {request.days_to_sell === 1 ? 'dia' : 'dias'}</span>
                          </div>
                        )}
                        
                        {request.request_13th_advance && (
                          <div className="flex items-center space-x-2 text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            <span>Antecipação do 13º solicitada</span>
                          </div>
                        )}
                        
                        <div className="text-gray-600">
                          <span className="font-medium">Total de dias: </span>
                          <span>{request.days_requested} dias de férias</span>
                          {request.sell_vacation_days && (
                            <span> + {request.days_to_sell} dias vendidos</span>
                          )}
                        </div>
                      </div>

                      {request.notes && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <p className="text-gray-600">Observações: {request.notes}</p>
                        </div>
                      )}
                      
                      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                        <span>Solicitado em: {formatDate(request.request_date)}</span>
                        {request.approved_date && (
                          <span>Aprovado em: {formatDate(request.approved_date)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Alterações */}
        <TabsContent value="alteracoes" className="space-y-6">
          <Card>
            <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <CardTitle>Solicitações de Alteração</CardTitle>
              <CardDescription className="text-blue-100">
                Pedidos de cancelamento ou alteração de datas de férias já aprovadas
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {(() => {
                if (changeRequests.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <RefreshCw className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 text-lg mb-2">Nenhuma solicitação de alteração</p>
                      <p className="text-gray-500 text-sm">
                        Solicitações de cancelamento ou alteração aparecerão aqui
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <p className="text-blue-800">
                        🔄 <strong>{changeRequests.length}</strong> solicitação(ões) de alteração
                      </p>
                    </div>

                    {changeRequests.map(cr => {
                      const employee = employees.find(e => e.id === cr.employee_id);
                      const original = cr.original_request;
                      const isCancellation = cr.change_type === 'cancellation';
                      const isDateChange = cr.change_type === 'date_change';

                      return (
                        <div key={cr.id} className={`border-2 rounded-lg p-4 hover:shadow-md transition-shadow ${isCancellation ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium text-lg">{cr.employee_name || employee?.nome}</h4>
                              <p className="text-sm text-gray-600">{employee?.cargo}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {isCancellation ? (
                                  <span className="text-red-600 font-medium text-sm">🚫 Cancelamento</span>
                                ) : (
                                  <span className="text-blue-600 font-medium text-sm">📅 Alteração de Datas</span>
                                )}
                                {getStatusBadge(cr.status === 'pending' ? 'pending' : cr.status === 'approved' ? 'approved' : 'rejected')}
                              </div>
                              {original && (
                                <p className="text-sm text-gray-700 mt-1">
                                  Período atual: {formatDate(original.start_date)} - {formatDate(original.end_date)}
                                  <span className="font-semibold ml-2">({original.days_requested} dias)</span>
                                </p>
                              )}
                              {isDateChange && (
                                <p className="text-sm text-blue-700 mt-1">
                                  ➡️ Novo período: {formatDate(cr.new_start_date)} - {formatDate(cr.new_end_date)}
                                </p>
                              )}
                              <p className="text-sm text-gray-600 mt-1">
                                📝 Motivo: {cr.reason}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Solicitado em: {formatDate(cr.request_date)}
                              </p>
                            </div>
                          </div>

                          {cr.status === 'pending' && (
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                onClick={() => approveChangeRequest(cr.id)}
                                className="bg-green-600 hover:bg-green-700 flex items-center space-x-2"
                              >
                                <CheckCircle className="h-4 w-4" />
                                <span>Aprovar</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  if (window.confirm('Tem certeza que deseja rejeitar esta solicitação?')) {
                                    rejectChangeRequest(cr.id);
                                  }
                                }}
                                className="flex items-center space-x-2"
                              >
                                <XCircle className="h-4 w-4" />
                                <span>Rejeitar</span>
                              </Button>
                            </div>
                          )}

                          {cr.status === 'approved' && (
                            <div className="mt-2 text-xs text-green-600">
                              ✅ Aprovado em {formatDate(cr.approved_date)}
                            </div>
                          )}
                          {cr.status === 'rejected' && (
                            <div className="mt-2 text-xs text-red-600">
                              ❌ Rejeitado
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Histórico */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Histórico de Férias</CardTitle>
                  <CardDescription className="text-purple-100">
                    Consulte aprovações/recusas e cadastre períodos de férias anteriores ao sistema
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowHistoricoModal(true)}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Cadastrar Histórico
                  </Button>
                  <Button
                    onClick={() => setShowImportModal(true)}
                    variant="outline"
                    className="bg-white/20 text-white border-white hover:bg-white/30"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Importar CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Seletor de Funcionário */}
              <div className="mb-6">
                <Label htmlFor="employee-select-history">Selecionar Funcionário</Label>
                <select
                  id="employee-select-history"
                  className="w-full p-2 border rounded-md mt-1"
                  onChange={(e) => {
                    setSelectedEmployee(e.target.value);
                    if (e.target.value) loadHistorico(e.target.value);
                  }}
                  value={selectedEmployee || ''}
                >
                  <option value="">Selecione um funcionário</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nome} - {emp.cargo}
                    </option>
                  ))}
                </select>
              </div>

              {/* Histórico de solicitações processadas */}
              {(() => {
                const solicitacoesProcessadas = vacationRequests
                  .filter((request) => ['approved', 'rejected'].includes(request.status))
                  .filter((request) => !selectedEmployee || request.employee_id === selectedEmployee)
                  .sort((a, b) => {
                    const dateA = new Date(a.approved_date || a.rejected_date || a.request_date || 0);
                    const dateB = new Date(b.approved_date || b.rejected_date || b.request_date || 0);
                    return dateB - dateA;
                  });

                return (
                  <div className="mb-8 space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold">Histórico de Solicitações Processadas</h3>
                      <p className="text-sm text-gray-500">
                        {selectedEmployee
                          ? 'Aprovações e recusas do colaborador selecionado'
                          : 'Aprovações e recusas de férias já processadas'}
                      </p>
                    </div>

                    {solicitacoesProcessadas.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 border rounded-lg bg-gray-50">
                        <Clock className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                        <p>Nenhuma solicitação processada encontrada</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {solicitacoesProcessadas.map((request) => {
                          const employee = employees.find((emp) => emp.id === request.employee_id);
                          const isApproved = request.status === 'approved';
                          const processedDate = request.approved_date || request.rejected_date;
                          const processedBy = request.approved_by_name || request.rejected_by_name || 'RH';

                          return (
                            <div
                              key={request.id}
                              className={`border rounded-lg p-4 transition-colors ${
                                isApproved ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="space-y-2">
                                  <div>
                                    <h4 className="font-medium text-lg">{request.employee_name || employee?.nome || 'Colaborador'}</h4>
                                    <p className="text-sm text-gray-600">{request.employee_position || employee?.cargo || 'Cargo não informado'}</p>
                                  </div>
                                  <p className="text-sm text-gray-700">
                                    📅 {formatDate(request.start_date)} - {formatDate(request.end_date)}
                                    <span className="font-semibold ml-2">({request.days_requested} dias)</span>
                                  </p>
                                  {request.notes && (
                                    <div className="rounded bg-white/70 p-2 text-sm text-gray-600">
                                      <span className="font-medium">Observações:</span> {request.notes}
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-500 space-y-1">
                                    <p>Solicitado em: {formatDate(request.request_date)}</p>
                                    <p>
                                      {isApproved ? 'Aprovado' : 'Recusado'} em: {formatDate(processedDate)} por {processedBy}
                                    </p>
                                  </div>
                                </div>
                                <div className="shrink-0">
                                  {getStatusBadge(request.status)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Lista de Históricos */}
              {selectedEmployee && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    Histórico de {employees.find(e => e.id === selectedEmployee)?.nome}
                  </h3>
                  
                  {historico.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p>Nenhum histórico cadastrado para este funcionário</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {historico.map((hist) => (
                        <div key={hist.id} className="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-purple-600">
                                  Período Aquisitivo:
                                </span>
                                <span>
                                  {formatDate(hist.periodo_aquisitivo_inicio)} a {formatDate(hist.periodo_aquisitivo_fim)}
                                </span>
                                <Badge variant={hist.status === 'gozado' ? 'default' : 'secondary'}>
                                  {hist.status === 'gozado' ? 'Gozado' : 
                                   hist.status === 'vendido' ? 'Vendido' : 'Pago em Dobro'}
                                </Badge>
                              </div>
                              
                              {hist.data_inicio_gozo && hist.data_fim_gozo && (
                                <div className="text-sm text-gray-600 mb-1">
                                  <CalendarDays className="h-4 w-4 inline mr-1" />
                                  Período de Gozo: {formatDate(hist.data_inicio_gozo)} a {formatDate(hist.data_fim_gozo)}
                                </div>
                              )}
                              
                              <div className="text-sm text-gray-600 mb-1">
                                📅 Dias Gozados: <span className="font-medium">{hist.dias_gozados} dias</span>
                              </div>
                              
                              {hist.observacoes && (
                                <div className="mt-2 p-2 bg-white rounded text-sm">
                                  <p className="text-gray-600">💬 {hist.observacoes}</p>
                                </div>
                              )}
                              
                              <div className="text-xs text-gray-500 mt-2">
                                Cadastrado em: {formatDate(hist.criado_em)}
                                {hist.criado_por_nome && ` por ${hist.criado_por_nome}`}
                              </div>
                            </div>
                            
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteHistorico(hist.id, selectedEmployee)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal: Cadastrar Histórico */}
      <Dialog open={showHistoricoModal} onOpenChange={setShowHistoricoModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cadastrar Histórico de Férias</DialogTitle>
            <DialogDescription>
              Cadastre um período de férias já gozado antes da implantação do sistema
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="hist-employee">Funcionário *</Label>
              <select
                id="hist-employee"
                className="w-full p-2 border rounded-md mt-1"
                value={historicoForm.employee_id}
                onChange={(e) => setHistoricoForm({...historicoForm, employee_id: e.target.value})}
              >
                <option value="">Selecione um funcionário</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nome} - {emp.cargo}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hist-periodo-inicio">Período Aquisitivo - Início *</Label>
                <Input
                  id="hist-periodo-inicio"
                  type="date"
                  value={historicoForm.periodo_aquisitivo_inicio}
                  onChange={(e) => setHistoricoForm({...historicoForm, periodo_aquisitivo_inicio: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="hist-periodo-fim">Período Aquisitivo - Fim *</Label>
                <Input
                  id="hist-periodo-fim"
                  type="date"
                  value={historicoForm.periodo_aquisitivo_fim}
                  onChange={(e) => setHistoricoForm({...historicoForm, periodo_aquisitivo_fim: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hist-gozo-inicio">Data Início Gozo (Opcional)</Label>
                <Input
                  id="hist-gozo-inicio"
                  type="date"
                  value={historicoForm.data_inicio_gozo}
                  onChange={(e) => setHistoricoForm({...historicoForm, data_inicio_gozo: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="hist-gozo-fim">Data Fim Gozo (Opcional)</Label>
                <Input
                  id="hist-gozo-fim"
                  type="date"
                  value={historicoForm.data_fim_gozo}
                  onChange={(e) => setHistoricoForm({...historicoForm, data_fim_gozo: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hist-dias">Dias Gozados</Label>
                <Input
                  id="hist-dias"
                  type="number"
                  min="1"
                  max="30"
                  value={historicoForm.dias_gozados}
                  onChange={(e) => setHistoricoForm({...historicoForm, dias_gozados: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label htmlFor="hist-status">Status</Label>
                <select
                  id="hist-status"
                  className="w-full p-2 border rounded-md"
                  value={historicoForm.status}
                  onChange={(e) => setHistoricoForm({...historicoForm, status: e.target.value})}
                >
                  <option value="gozado">Gozado</option>
                  <option value="vendido">Vendido</option>
                  <option value="pago_em_dobro">Pago em Dobro</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="hist-obs">Observações</Label>
              <textarea
                id="hist-obs"
                className="w-full p-2 border rounded-md mt-1"
                rows="3"
                value={historicoForm.observacoes}
                onChange={(e) => setHistoricoForm({...historicoForm, observacoes: e.target.value})}
                placeholder="Informações adicionais sobre este período..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoricoModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateHistorico} className="bg-purple-600 hover:bg-purple-700">
              Cadastrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Importar CSV */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar Histórico via CSV</DialogTitle>
            <DialogDescription>
              Faça upload de um arquivo CSV com o histórico de férias
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">
                  {csvFile ? csvFile.name : 'Clique para selecionar arquivo CSV'}
                </p>
              </label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <p className="font-semibold mb-2">📋 Formato do CSV:</p>
              <code className="block bg-white p-2 rounded text-xs">
                cpf,periodo_aquisitivo_inicio,periodo_aquisitivo_fim,data_inicio_gozo,data_fim_gozo,dias_gozados,status,observacoes
              </code>
              <p className="mt-2 text-gray-600">
                <strong>Campos obrigatórios:</strong> cpf (ou employee_id), periodo_aquisitivo_inicio, periodo_aquisitivo_fim
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowImportModal(false);
              setCsvFile(null);
            }}>
              Cancelar
            </Button>
            <Button onClick={handleImportCSV} disabled={!csvFile} className="bg-purple-600 hover:bg-purple-700">
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VacationControlPage;
